import { useEffect, useRef } from "react";
import type { FollowedItem, FollowsResponse } from "@skills-marketplace/contracts/contract-11-follows";
import EmptyState from "../components/EmptyState";
import { useFollows } from "../lib/follows-context";
import { postVisit } from "../lib/follow-client";
import { detailHrefForInstallKey } from "../lib/catalog-context";
import { reportEvent } from "../lib/newrelic";
import styles from "./FollowingPage.module.css";

const CHANGE_LABEL = {
  new: "New since your last visit",
  updated: "Updated since your last visit",
  inactive: "Now inactive",
} as const;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function provenance(item: FollowedItem, displayNameOf: (installName: string) => string): string | null {
  if (item.kind === "plugin") {
    const count = item.includedSkills?.length ?? 0;
    return count > 0 ? `Following the whole plug-in. This includes its ${count} skills.` : null;
  }
  if (!item.viaPlugin) return null;
  const plugin = displayNameOf(item.viaPlugin);
  return item.followedDirectly
    ? `Following this skill only, from the ${plugin} plug-in`
    : `Following via the ${plugin} plug-in`;
}

function FollowedItemCard({
  item,
  response,
  onUnfollow,
}: {
  item: FollowedItem;
  response: FollowsResponse;
  onUnfollow: (item: FollowedItem) => void;
}) {
  const displayNameOf = (installName: string) =>
    response.items.find((candidate) => candidate.installName === installName)?.displayName ?? installName;
  const inactive = item.status === "inactive";
  const note = provenance(item, displayNameOf);

  return (
    <li
      className={`${styles.card} ${inactive ? styles.inactive : ""} ${item.change ? styles.changed : ""}`}
    >
      <div className={styles.header}>
        <span className={styles.kind}>{item.kind === "plugin" ? "Plugin" : "Skill"}</span>
        {item.change && <span className={styles.badge}>{CHANGE_LABEL[item.change]}</span>}
        <span className={styles.version}>v{item.version}</span>
      </div>

      <h2 className={styles.name}>
        {inactive ? (
          item.displayName
        ) : (
          <a href={detailHrefForInstallKey(item.installName)}>{item.displayName}</a>
        )}
      </h2>
      <p className={styles.repo}>
        {item.orgOrOwner}/{item.repoName}
      </p>

      {inactive ? (
        <div className={styles.unavailable}>
          <p>This item is no longer available.</p>
          {item.inactiveSince && <p>Inactive since {formatDate(item.inactiveSince)}</p>}
        </div>
      ) : (
        <p className={styles.updated}>Last updated {formatDate(item.lastUpdatedAt)}</p>
      )}

      {note && <p className={styles.note}>{note}</p>}

      <div className={styles.actions}>
        {item.followedDirectly && (
          <button type="button" className={styles.unfollow} onClick={() => onUnfollow(item)}>
            Unfollow {item.displayName}
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * The caller's followed items with change flags since their last visit. Entering the page
 * refreshes the list; the visit is recorded once per response shown, so the flags clear on
 * the next visit rather than on this render.
 */
export default function FollowingPage() {
  const { enabled, status, response, refresh, toggle } = useFollows();
  const visitedAsOf = useRef<string | null>(null);

  useEffect(() => {
    void refresh();
    // Only on entering the page; the provider keeps the list current after writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!response || visitedAsOf.current === response.asOf) return;
    visitedAsOf.current = response.asOf;
    void postVisit({ asOf: response.asOf, familySnapshot: response.familySnapshot });
    reportEvent("marketplace_following_visit", { totalCount: response.totalCount, changedCount: response.changedCount });
  }, [response]);

  if (!enabled) return null;

  return (
    <section className={styles.page}>
      <h1>Following</h1>
      <p className={styles.blurb}>
        Changes to what you follow show here the next time you open this page. Only you can see this list.
      </p>

      {status === "loading" && !response && <p role="status">Loading your follows…</p>}

      {status === "error" && !response && (
        <div role="alert" className={styles.retry}>
          <p>We couldn't load your follows.</p>
          <button type="button" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      )}

      {response && response.totalCount === 0 && (
        <EmptyState message="You aren't following anything yet.">
          <p>
            <a href="#/">Browse the catalog</a> and choose Follow on a plug-in or skill to keep track of it here.
          </p>
        </EmptyState>
      )}

      {response && response.totalCount > 0 && (
        <>
          <p className={styles.summary}>
            {response.totalCount} followed
            {response.changedCount > 0 && (
              <span className={styles.changedPill}>{response.changedCount} changed since your last visit</span>
            )}
          </p>
          <ul className={styles.list}>
            {response.items.map((item) => (
              <FollowedItemCard
                key={item.installName}
                item={item}
                response={response}
                onUnfollow={(target) => void toggle(target.installName, target.displayName, false)}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
